import { handleFieldNavigation } from '@features/admin/trading/utils';
import { useURLSearchParams } from '@hooks/useURLSearchParams';
import { ENDPOINT_MEASURES_COLUMNS, ENDPOINT_MEASURES_FILTERS, EndpointMeasure } from '@protos/endpointMeasures';
import { Filter, FilterType } from '@protos/filter';
import { BlotterWidget } from '@shared/components/BlotterWidget';
import { CircularLoader } from '@shared/components/CircularLoader';
import { useAdminApi } from '@shared/hooks/useAdminApi';
import theme from '@shared/themes/darkTheme';
import { CellClickedEvent, ColDef, GridApi, RowClassParams } from 'ag-grid-enterprise';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsync } from 'react-use';

const getRowId = ({ data }): string => [data.user_id, data.path, data.timestamp, data.duration].join(',');

const defaultColDef = () => {
  return {
    filter: false,
    floatingFilter: false,
    resizable: true,
    enableCellChangeFlash: true,
  } as ColDef;
};

const getRowStyle = (params: RowClassParams) => {
  if (params.data?.duration >= 1) {
    return { background: theme.palette.error.main, color: '#000' };
  }
  if (params.data?.duration >= 0.2) {
    return { background: theme.palette.warning.light, color: '#000' };
  }

  return undefined;
};

export const EndpointMeasures = () => {
  const { apiClient } = useAdminApi();
  const dataLoader = apiClient?.endpointMeasuresLoader();
  const [blotterApi, setBlotterApi] = useState<GridApi<EndpointMeasure | null>>();
  const [lastDataFetch, setLastDataFetch] = useState('');
  const { urlParams } = useURLSearchParams();
  const navigate = useNavigate();

  const onGetGridApi = useCallback((gridApi: GridApi<EndpointMeasure | null>) => {
    if (gridApi) {
      setBlotterApi(gridApi);
      setLastDataFetch(new Date().toISOString());
    }
  }, []);

  const productsState = useAsync(async () => {
    const products = await apiClient?.productsLoader().loadAllData();
    return products?.map(product => product.symbol) || [];
  }, [apiClient]);

  const filters: Filter[] = useMemo(
    () => [
      ...ENDPOINT_MEASURES_FILTERS,
      { type: FilterType.AUTOCOMPLETE, name: 'product_symbol', label: 'Product Symbol', options: productsState.value },
    ],
    [productsState]
  );

  const onCellClicked = useCallback(
    (params: CellClickedEvent) => {
      handleFieldNavigation(navigate, 'user_id', params.data?.user_id);
    },
    [navigate]
  );

  const fetchNewData = useCallback(
    async (lastDataFetch: string) => {
      if (!blotterApi || !apiClient || urlParams.end) return;

      const newData = await apiClient.endpointMeasuresLoader({ ...urlParams, start: lastDataFetch }).loadAllData();
      blotterApi.applyTransaction({ add: newData, addIndex: 0 });
      setLastDataFetch(new Date().toISOString());

      setTimeout(() => {
        newData.forEach(data => {
          const newRowNode = blotterApi.getRowNode(getRowId({ data }));
          if (newRowNode) blotterApi.flashCells({ rowNodes: [newRowNode], fadeDuration: 2000 });
        });
      }, 200);
    },
    [apiClient, blotterApi, urlParams]
  );

  useEffect(() => {
    const interval = setInterval(() => {
      fetchNewData(lastDataFetch);
    }, 5000);

    return () => clearInterval(interval);
  }, [apiClient, blotterApi, urlParams, lastDataFetch]);

  if (!dataLoader || !filters[filters.length - 1].options?.length) return <CircularLoader />;

  return (
    <BlotterWidget
      getGridApi={onGetGridApi}
      dataLoader={dataLoader}
      columnDefs={ENDPOINT_MEASURES_COLUMNS}
      getRowId={getRowId}
      getRowStyle={getRowStyle}
      animateRows={true}
      filters={filters}
      defaultColDef={defaultColDef()}
      autoSizeStrategy={{ type: 'fitCellContents' }}
      enableCellTextSelection
      onCellClicked={onCellClicked}
    />
  );
};
